import { useState } from 'react';
import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx';
import { Upload, CheckCircle, AlertCircle, FileText, Loader } from 'lucide-react';

const SHEETS = [
  { key: 'suppliers',    label: 'Suppliers',           sheetName: 'Suppliers' },
  { key: 'products',     label: 'Skincare Products',   sheetName: 'Skincare Products' },
  { key: 'treatments',  label: 'Treatments',           sheetName: 'Treatments' },
  { key: 'supplements', label: 'Cytoplan Supplements', sheetName: 'Cytoplan Supplements' },
  { key: 'stock',       label: 'Stock',                sheetName: 'Stock' },
];

const parseSheet = (wb, sheetName) => {
  const ws = wb.Sheets[sheetName];
  if (!ws) return [];
  return XLSX.utils.sheet_to_json(ws, { defval: '' });
};

const str = (v) => (v === null || v === undefined) ? '' : String(v).trim();

const mapSupplier = (row) => {
  const addr = str(row['Address']);
  const notes = [addr, str(row['Notes'])].filter(Boolean).join(' | ');
  return {
    name: str(row['Supplier Name']),
    accountNo: str(row['Account Number']),
    contact: str(row['Contact Name']),
    email: str(row['Email']),
    phone: str(row['Phone']),
    notes,
  };
};

const mapProduct = (row) => ({
  name: str(row['Product Name']),
  brand: str(row['Brand']),
  category: str(row['Category']),
  description: str(row['Description']),
  howToUse: str(row['How To Use']),
  whenToUse: str(row['When To Use']),
  rrp: str(row['RRP (£)']),
  tradeIncVat: str(row['Your Cost (£)']),
  supplier: str(row['Supplier Name']),
  notes: str(row['Notes']),
  status: 'Active',
  type: 'Skincare',
  inStock: true,
});

const mapTreatment = (row) => ({
  name: str(row['Treatment Name']),
  category: str(row['Category']),
  duration: str(row['Duration (mins)']),
  price: str(row['Price (£)']),
  description: str(row['Description']),
  notes: str(row['Notes']),
});

const mapSupplement = (row) => ({
  name: str(row['Product Name']),
  code: str(row['Product Code']),
  description: str(row['Description']),
  indications: str(row['Key Indications']),
  dosage: str(row['Suggested Dosage']),
  price: str(row['Price (£)']),
  notes: str(row['Notes']),
});

const mapStock = (row) => ({
  name: str(row['Item Name']),
  category: str(row['Category']),
  supplier: str(row['Supplier Name']),
  unit: str(row['Unit']),
  costPerUnit: str(row['Cost Per Unit (£)']),
  minQty: str(row['Min Stock Level']),
  currentQty: str(row['Current Stock']),
  notes: [
    row['Units Per Pack'] ? `Units per pack: ${row['Units Per Pack']}` : '',
    str(row['Notes']),
  ].filter(Boolean).join(' | '),
});

const MAPPERS = {
  suppliers:   { fn: mapSupplier,   pk: 'Supplier Name',  table: 'suppliers' },
  products:    { fn: mapProduct,    pk: 'Product Name',   table: 'products' },
  treatments:  { fn: mapTreatment,  pk: 'Treatment Name', table: 'treatments' },
  supplements: { fn: mapSupplement, pk: 'Product Name',   table: 'supplements' },
  stock:       { fn: mapStock,      pk: 'Item Name',      table: 'stock' },
};

export default function Import() {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [dragging, setDragging] = useState(false);

  const processFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
      const parsed = {};
      const sel = {};
      SHEETS.forEach(({ key, sheetName }) => {
        parsed[key] = parseSheet(wb, sheetName);
        sel[key] = true;
      });
      setPreview(parsed);
      setSelected(sel);
      setResults(null);
    };
    reader.readAsArrayBuffer(file);
  };

  const validRows = (key) => {
    if (!preview?.[key]) return [];
    const { fn, pk } = MAPPERS[key];
    return preview[key]
      .map(fn)
      .filter(r => r.name && r.name.length > 0 && r.name !== 'Example Name');
  };

  const importAll = async () => {
    setImporting(true);
    const res = {};
    for (const key of Object.keys(MAPPERS)) {
      if (!selected[key]) { res[key] = { skipped: true }; continue; }
      const rows = validRows(key);
      if (rows.length === 0) { res[key] = { count: 0 }; continue; }
      let count = 0, errors = 0;
      for (const row of rows) {
        const { error } = await supabase.from(MAPPERS[key].table).insert([row]);
        if (error) errors++;
        else count++;
      }
      res[key] = { count, errors };
    }
    setResults(res);
    setImporting(false);
  };

  const totalRows = preview
    ? Object.keys(MAPPERS).filter(k => selected[k]).reduce((s, k) => s + validRows(k).length, 0)
    : 0;

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Import from Excel</h2>
          <p className="section-sub">Upload your DA-Hub-Database-Template.xlsx to populate the system</p>
        </div>
      </div>

      {/* Instructions */}
      <div className="import-instructions">
        <FileText size={16} />
        <div>
          <strong>Before uploading:</strong> open your Excel template, delete the grey example rows in each sheet, then save. The app will import whatever is in the file.
        </div>
      </div>

      {/* Drop zone */}
      {!preview && (
        <div
          className={`drop-zone ${dragging ? 'dragging' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]); }}
        >
          <Upload size={32} style={{ color: 'var(--gold)' }} />
          <p>Drag & drop your Excel file here</p>
          <p className="soft" style={{ fontSize: '12px' }}>or</p>
          <label className="btn-gold" style={{ cursor: 'pointer', padding: '8px 20px' }}>
            Choose File
            <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
              onChange={e => processFile(e.target.files[0])} />
          </label>
          <p className="soft" style={{ fontSize: '11px', marginTop: '8px' }}>DA-Hub-Database-Template.xlsx</p>
        </div>
      )}

      {/* Preview */}
      {preview && !results && (
        <>
          <div className="import-preview">
            {SHEETS.map(({ key, label }) => {
              const rows = validRows(key);
              return (
                <div key={key} className="import-sheet-row">
                  <label className="import-check-label">
                    <input
                      type="checkbox"
                      checked={!!selected[key]}
                      onChange={e => setSelected(s => ({ ...s, [key]: e.target.checked }))}
                    />
                    <span className="import-sheet-name">{label}</span>
                    <span className={`import-count ${rows.length > 0 ? 'has-data' : 'no-data'}`}>
                      {rows.length} row{rows.length !== 1 ? 's' : ''}
                    </span>
                  </label>
                  {rows.length > 0 && (
                    <div className="import-sample">
                      {rows.slice(0, 3).map((r, i) => (
                        <span key={i} className="import-tag">{r.name}</span>
                      ))}
                      {rows.length > 3 && <span className="soft">+{rows.length - 3} more</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="form-actions" style={{ marginTop: '1.5rem' }}>
            <button className="btn-ghost" onClick={() => setPreview(null)}>
              Choose different file
            </button>
            <button
              className="btn-gold"
              onClick={importAll}
              disabled={importing || totalRows === 0}
            >
              {importing
                ? <><Loader size={14} /> Importing...</>
                : <><Upload size={14} /> Import {totalRows} records</>}
            </button>
          </div>
        </>
      )}

      {/* Results */}
      {results && (
        <div className="import-results">
          <h3 style={{ marginBottom: '1rem' }}>Import complete</h3>
          {SHEETS.map(({ key, label }) => {
            const r = results[key];
            if (!r || r.skipped) return null;
            return (
              <div key={key} className="import-result-row">
                {r.errors > 0
                  ? <AlertCircle size={16} style={{ color: 'var(--amber)' }} />
                  : <CheckCircle size={16} style={{ color: 'var(--green)' }} />}
                <span><strong>{label}:</strong> {r.count} imported{r.errors > 0 ? `, ${r.errors} failed` : ''}</span>
              </div>
            );
          })}
          <div className="form-actions" style={{ marginTop: '1.5rem' }}>
            <button className="btn-ghost" onClick={() => { setPreview(null); setResults(null); }}>
              Import another file
            </button>
            <button className="btn-gold" onClick={() => window.location.reload()}>
              Done — reload app
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
